'use client'

import { CLIENT_LOGIN_CODE_LENGTH } from '@tebuto/constants/client-login-code'
import FormField from '@tebuto/forms/Field'
import { EMAIL_REGEX } from '@tebuto/functions/validate'
import MediumText from '@tebuto/typography/MediumText'
import SmallText from '@tebuto/typography/SmallText'
import { WidgetButton } from '@tebuto/widget/WidgetButton'
import { Form, Formik } from 'formik'
import { type JSX, useContext, useState } from 'react'
import OtpInput from 'react-otp-input'
import { object, string } from 'yup'
import BookingBackButton from '../../booking/BookingBackButton'
import { WidgetAuthContext } from './WidgetAuthProvider'

interface LoginViewProps {
    onDone: () => void
    onBack: () => void
}

interface EmailFormValues {
    email: string
}

const emailSchema = object().shape({
    email: string().matches(EMAIL_REGEX, 'Ungültige E-Mail-Adresse').required('Pflichtfeld')
})

export default function LoginView({ onDone, onBack }: LoginViewProps): JSX.Element {
    const { requestLoginCode, confirmLoginCode } = useContext(WidgetAuthContext)
    const [step, setStep] = useState<'email' | 'otp'>('email')
    const [email, setEmail] = useState<string>('')
    const [otp, setOtp] = useState<string>('')
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [error, setError] = useState<string | null>(null)

    async function submitEmail(values: EmailFormValues) {
        setError(null)
        setIsSubmitting(true)
        try {
            await requestLoginCode(values.email)
            setEmail(values.email)
            setStep('otp')
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Fehler beim Anfordern des Codes.')
        } finally {
            setIsSubmitting(false)
        }
    }

    async function submitOtp() {
        if (otp.length !== CLIENT_LOGIN_CODE_LENGTH) return
        setError(null)
        setIsSubmitting(true)
        try {
            await confirmLoginCode(email, otp)
            onDone()
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Code konnte nicht bestätigt werden.')
        } finally {
            setIsSubmitting(false)
        }
    }

    function backToEmail() {
        setStep('email')
        setOtp('')
        setError(null)
    }

    return (
        <div className="flex flex-col w-full max-w-md mx-auto" data-testid="widget-login-view">
            <BookingBackButton onClick={step === 'otp' ? backToEmail : onBack} label="Zurück" isLoading={false} />
            <div className="flex flex-col gap-6 mt-6 border widget-border rounded-2xl widget-bg-field p-6 sm:p-8 shadow-sm">
                <div className="h-1 -m-6 sm:-m-8 mb-0 sm:mb-0 rounded-t-2xl widget-bg-primary-500" />
                <div className="flex flex-col gap-2">
                    <h2 className="text-2xl font-semibold widget-text-primary">
                        {step === 'email' ? 'Anmelden' : 'Code eingeben'}
                    </h2>
                    <MediumText className="widget-text-secondary">
                        {step === 'email'
                            ? 'Geben Sie Ihre E-Mail-Adresse ein. Wir senden Ihnen einen Bestätigungscode.'
                            : `Wir haben einen ${CLIENT_LOGIN_CODE_LENGTH}-stelligen Code an ${email} gesendet.`}
                    </MediumText>
                </div>

                {step === 'email' ? (
                    <Formik<EmailFormValues>
                        initialValues={{ email }}
                        validationSchema={emailSchema}
                        validateOnMount
                        onSubmit={submitEmail}
                    >
                        {({ isValid }) => (
                            <Form className="flex flex-col gap-4" data-testid="widget-login-email-form">
                                <FormField
                                    name="email"
                                    label="Ihre E-Mail-Adresse"
                                    type="text"
                                    placeholder="email@example.com"
                                    autoComplete="email"
                                    fieldClassName="widget-bg-field widget-border widget-text-primary"
                                    labelClassName="widget-text-secondary"
                                    mandatory
                                />
                                {error && (
                                    <SmallText className="text-red-500" data-testid="widget-login-error">
                                        {error}
                                    </SmallText>
                                )}
                                <WidgetButton
                                    type="submit"
                                    disabled={!isValid || isSubmitting}
                                    isLoading={isSubmitting}
                                    data-testid="widget-login-request-code"
                                >
                                    Code anfordern
                                </WidgetButton>
                            </Form>
                        )}
                    </Formik>
                ) : (
                    <div className="flex flex-col gap-4" data-testid="widget-login-otp-form">
                        <OtpInput
                            value={otp}
                            onChange={(value) => setOtp(value.replace(/\D/g, ''))}
                            numInputs={CLIENT_LOGIN_CODE_LENGTH}
                            inputType="tel"
                            renderInput={(inputProps, index) => (
                                <input
                                    {...inputProps}
                                    data-testid={`widget-login-otp-${index}`}
                                    className="w-full! py-2 sm:h-12 px-0 text-center text-lg! rounded-lg border widget-border widget-bg-field widget-text-primary focus:outline-none focus:ring-2 focus:widget-border-primary-500"
                                />
                            )}
                            containerStyle="grid grid-cols-6 gap-2 w-full"
                        />
                        {error && (
                            <SmallText className="text-red-500" data-testid="widget-login-error">
                                {error}
                            </SmallText>
                        )}
                        <WidgetButton
                            type="button"
                            onClick={submitOtp}
                            disabled={otp.length !== CLIENT_LOGIN_CODE_LENGTH || isSubmitting}
                            isLoading={isSubmitting}
                            data-testid="widget-login-submit-otp"
                        >
                            Anmelden
                        </WidgetButton>
                        <button
                            type="button"
                            className="text-xs underline widget-text-secondary self-center"
                            onClick={backToEmail}
                        >
                            Andere E-Mail-Adresse verwenden
                        </button>
                    </div>
                )}
            </div>
        </div>
    )
}
